/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {HttpEventType} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../../common/constants';
import {SheetSelectorComponent} from '../../../../core/components/sheet-selector/sheet-selector.component';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../../core/models/column-header.model';
import {WorksheetExportRequest} from '../../../../core/models/worksheet-export-request.model';
import {SegmentReport} from '../../../../models/reports/segment-report.model';
import {SelectableOptions} from '../../../../models/selectable-options';
import {ExcelExportService} from '../../../../services/excel-export.service';
import {ManageBrandFamilyService} from '../../../../services/manage-brand-family-service';

@Component({
    selector: 'app-segment-report',
    templateUrl: './segment-report.component.html',
    styleUrls: ['./segment-report.component.scss'],
    standalone: false
})
export class SegmentReportComponent implements OnInit {

  constructor(public snackBar: FabMessageService,
              private readonly brandfamService: ManageBrandFamilyService,
              public readonly excelService: ExcelExportService,
              private readonly dialog: MatDialog) {
  }

  showSpinner: boolean;

  segmentReportData: SegmentReport[] = [];
  segmentReportMap: Map<number, SegmentReport> = new Map();
  unchangedSegments: SegmentReport[] = [];
  unchangedSegmentsMap: Map<number, SegmentReport> = new Map();
  validSegments: string[] = [];
  invalidSegments: SegmentReport[] = [];
  excelSegmentReportData: Map<string, SegmentReport[]> = new Map<string, SegmentReport[]>();
  file: File;
  sheets: SelectableOptions = new SelectableOptions();
  worksheetExportRequest: WorksheetExportRequest;
  segmentViolationExportRequest: WorksheetExportRequest;
  segmentReportColumnHeaders: ColumnHeader[] = [
    {name: 'brandfamID', label: 'BRANDFAM_ID'},
    {name: 'brandfam', label: 'BRANDFAM'},
    {name: 'gfsSevenKeyField.plngMkt', label: 'PLNGMKT'},
    {name: 'gfsSevenKeyField.mktDiv', label: 'MKTDIV'},
    {name: 'gfsSevenKeyField.brand', label: 'BRAND'},
    {name: 'gfsSevenKeyField.prgmCode', label: 'PRGMCODE'},
    {name: 'gfsSevenKeyField.styleCode', label: 'STYLECODE'},
    {name: 'gfsSevenKeyField.bodyStyle', label: 'BODYSTYLE'},
    {name: 'gfsSevenKeyField.lbrand', label: 'LBRAND'},
    {name: 'globalSegment', label: 'GLOBAL_SEGMENT'},
    {name: 'propSegment', label: 'PROP_SEG'}
  ];
  violationSegmentColumnHeaders: ColumnHeader[] = [
    {name: 'brandfamID', label: 'BRANDFAM_ID'},
    {name: 'globalSegment', label: 'GLOBAL_SEGMENT'},
    {name: 'propSegment', label: 'PROP_SEG'}
  ];


  segmentReportColumns: ColDef[] = [
    {field: 'brandfamID', headerName: 'BRANDFAM_ID', filter: AutocompleteFilterComponent},
    {field: 'brandfam', headerName: 'BRANDFAM', filter: AutocompleteFilterComponent},
    {field: 'gfsSevenKeyField.plngMkt', headerName: 'PLNGMKT', filter: AutocompleteFilterComponent},
    {field: 'gfsSevenKeyField.mktDiv', headerName: 'MKTDIV', filter: AutocompleteFilterComponent},
    {field: 'gfsSevenKeyField.brand', headerName: 'BRAND', filter: AutocompleteFilterComponent},
    {field: 'gfsSevenKeyField.prgmCode', headerName: 'PRGMCODE', filter: AutocompleteFilterComponent},
    {field: 'gfsSevenKeyField.styleCode', headerName: 'STYLECODE', filter: AutocompleteFilterComponent},
    {field: 'gfsSevenKeyField.bodyStyle', headerName: 'BODYSTYLE', filter: AutocompleteFilterComponent},
    {field: 'gfsSevenKeyField.lbrand', headerName: 'LBRAND', filter: AutocompleteFilterComponent},
    {field: 'globalSegment', headerName: 'GLOBAL_SEGMENT', filter: AutocompleteFilterComponent},
    {field: 'propSegment', headerName: 'PROP_SEG', filter: AutocompleteFilterComponent},
  ];

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };


  ngOnInit(): void {
    this.setVariables();
    this.getValidSegments();
    this.getSegmentReport();
  }

  /**
   *  sets initial values for variables on init
   */
  setVariables(): void {
    this.showSpinner = false;
    this.segmentReportMap.clear();
    this.unchangedSegmentsMap.clear();
  }

  /**
   * Fetches and displays the segment report
   */
  getSegmentReport(): void {
    this.showSpinner = true;
    this.brandfamService.getSegmentReport().subscribe(
      data => {
        this.segmentReportData = data;
        this.unchangedSegments = [];
        this.segmentReportData.forEach(segment => {
          this.unchangedSegments.push(segment);
          this.segmentReportMap.set(segment.brandfamID, segment);
        });
        this.unchangedSegmentsMap = new Map<number, SegmentReport>(this.segmentReportMap);
      },
      err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      () => {
        this.excelSegmentReportData[this.sheets.selectedOptions[0]] = this.segmentReportData;
        this.setSegmentReportExportRequest();
        this.showSpinner = false;
      }
    );
  }

  /**
   * saves the segments that have been changed
   * @return void
   */
  saveSegmentChanges(): void {
    if (this.validateSegmentChanges()) {
      const changedSegments = this.getSegmentChanges();
      if (changedSegments.length === 0) {
        this.snackBar.displayMessage('No changes made', DISMISS_MSG);
        this.showSpinner = false;
        return;
      }
      this.brandfamService.updateSegment(changedSegments).subscribe({
        error: err => {
          this.snackBar.displayError(err, DISMISS_MSG);
          this.showSpinner = false;
        },
        complete: () => {
          this.snackBar.displaySuccess(`Updated ${changedSegments.length} objects`, DISMISS_MSG);
          this.getSegmentReport();
        }
      });
    } else {
      this.showSpinner = false;
      this.setViolationSegmentReportExportRequest();
      this.snackBar.displayError('Fix invalid EV/ICE segments before proceeding', DISMISS_MSG);
    }
  }

  /**
   * Returns the segments that have been altered
   * Replaces user inputted ev/ice segment with proper casing ev/ice segment
   * ex. user input 'pickup-b-ice' -> 'PICKUP-B-ICE
   * ex. 'LUXURY SUV-3-ice' -> 'Luxury SUV-3-ICE'
   * @return the segments that have been altered
   */
  getSegmentChanges(): SegmentReport[] {
    const changedSegments: SegmentReport[] = [];
    for (const [id, segment] of this.segmentReportMap) {
      const unchangedSegment = this.unchangedSegmentsMap.get(id);
      if (segment.propSegment !== unchangedSegment.propSegment) {
        const validCaseSegment = this.validSegments.find(validSegment =>
          segment.propSegment.toLowerCase() === validSegment.toLowerCase());
        if (validCaseSegment !== unchangedSegment.propSegment) {
          segment.propSegment = validCaseSegment;
          changedSegments.push(segment);
        }
      }
    }
    return changedSegments;
  }

  /**
   * Gets a list of valid EV/ICE segments
   */
  getValidSegments(): void {
    this.brandfamService.getValidSegments().subscribe(
      data => {
        this.validSegments = data;
      },
      err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * checks changed segments and adds any invalid changes to the invalid list
   * @return true if all are valid, false otherwise
   */
  validateSegmentChanges(): boolean {
    this.invalidSegments = [];
    const validSegmentsLowerCase = this.validSegments.map((str) => str.toLowerCase());
    for (const segment of this.segmentReportMap.values()) {
      if (segment.propSegment.toLowerCase() === 'nd' ||
        !(segment.propSegment.toLowerCase().includes(segment.globalSegment.toLowerCase())) ||
        !(validSegmentsLowerCase.includes(segment.propSegment.toLowerCase())) ||
        segment.propSegment.length === 0 ||
        !(this.unchangedSegmentsMap.has(segment.brandfamID))) {
        this.invalidSegments.push(segment);
      }
    }
    return this.invalidSegments.length <= 0;
  }

  /**
   * Event handler for when an upload event is emitted. Receives the Segment report
   * data in the event. Displays the results to the user, whether any validation error occurs
   * or any records were successfully updated/added/deleted.
   * @param event The event that is emitted by the Upload button component
   */
  upload(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    this.dialog.open(SheetSelectorComponent, {
      data: {
        sheets: this.sheets.options,
        isMultiSelect: false
      },
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        // Clear out the selected options before setting them in case the user already uploaded a file and
        // selected an option previously
        this.sheets.selectedOptions = [];
        if (data) {
          this.sheets.selectedOptions = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      () => {
        if (this.sheets.selectedOptions.length > 0) {
          this.getUploadedData();
        }
      }
    );
  }

  /**
   * Get the segment report data from the Excel file that was uploaded
   */
  getUploadedData(): void {
    this.showSpinner = true;
    this.brandfamService.transform(this.file, this.sheets.selectedOptions).subscribe(
      (e: any) => {
        // Get Segment Report data from the uploaded Excel file
        if (e.type === HttpEventType.Response) {
          this.excelSegmentReportData = e.body;
        }
      },
      (error) => {
        if (error.status === 0) {
          this.snackBar.displayError('Error occurred. Please re-upload file.', DISMISS_MSG);
        } else {
          this.snackBar.displayError(error, DISMISS_MSG);
        }
        this.showSpinner = false;
      },
      () => {
        this.segmentReportData = [...this.excelSegmentReportData[this.sheets.selectedOptions[0]]];
        this.segmentReportMap.clear();
        this.segmentReportData.forEach(segment => this.segmentReportMap.set(segment.brandfamID, segment));
        this.saveSegmentChanges();
        this.setSegmentReportExportRequest();
      });
  }

  /**
   * Helper function used to set the export request
   */
  setSegmentReportExportRequest(): void {
    const excelData = new Map<string, SegmentReport>();
    excelData['Segment Report'] = this.excelSegmentReportData[this.sheets.selectedOptions[0]];
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');

    this.worksheetExportRequest = this.excelService.createExportRequestBody('SegmentTable_' + currDate,
      excelData,
      [['Segment Report', this.segmentReportColumnHeaders]]);
  }

  /**
   * Helper function used to set the export request
   */
  setViolationSegmentReportExportRequest(): void {
    const excelData = new Map<string, any>();
    excelData['Segment Violation Report'] = this.invalidSegments.map(segment => ({
      brandfamID: segment.brandfamID,
      globalSegment: segment.globalSegment,
      propSegment: segment.propSegment
    }));
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    this.segmentViolationExportRequest = this.excelService.createExportRequestBody('SegmentViolations_' + currDate,
      excelData,
      [['Segment Violation Report', this.violationSegmentColumnHeaders]]);
    this.excelService.transformToExcel(this.segmentViolationExportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, this.segmentViolationExportRequest.fileName);
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      });
  }
}
